// "Remember me" support: the auth keys below are session-scoped when the
// login checkbox was left unchecked (cleared when the browser/tab closes),
// persistent (localStorage) otherwise. Everything else stored through this
// module (UI preferences, filters, …) is unaffected and always persistent —
// only these specific keys ever move to sessionStorage.
const SESSION_SCOPED_KEYS = new Set(['token', 'refreshToken', 'authUser', 'roleName', 'resourcePermissions']);
const REMEMBER_KEY = 'rememberMe';

/** No marker yet means "remembered" — preserves existing sessions' behavior. */
const isRemembered = () => {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== 'false';
  } catch {
    return true;
  }
};

const backendFor = (key) => (SESSION_SCOPED_KEYS.has(key) && !isRemembered() ? sessionStorage : localStorage);
const otherBackendFor = (key) => (backendFor(key) === localStorage ? sessionStorage : localStorage);

/** Thin, safe wrapper around localStorage/sessionStorage (JSON-aware, never throws). */
export const storage = {
  get(key) {
    try {
      const raw = backendFor(key).getItem(key);
      if (raw == null) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      const toStore = typeof value === 'string' ? value : JSON.stringify(value);
      backendFor(key).setItem(key, toStore);
    } catch {
      /* storage full / unavailable — ignore */
    }
  },
  remove(key) {
    try {
      backendFor(key).removeItem(key);
    } catch {
      /* ignore */
    }
  },
  clear() {
    try {
      localStorage.clear();
    } catch {
      /* ignore */
    }
  },
  /**
   * Record the login's "Remember Me" choice and move any stale copy of the
   * session-scoped auth keys out of the backend that's no longer active, so a
   * later bare `storage.get()` never reads leftover data from a previous
   * choice.
   */
  setRememberMe(remembered) {
    try {
      localStorage.setItem(REMEMBER_KEY, remembered ? 'true' : 'false');
    } catch {
      /* ignore */
    }
    SESSION_SCOPED_KEYS.forEach((key) => {
      try {
        otherBackendFor(key).removeItem(key);
      } catch {
        /* ignore */
      }
    });
  },
};

export default storage;
